import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router';

import type { Conversation, ConversationFrom } from '@customTypes/conversation';

import { PAGINATION_WINDOW_SIZE } from '@constants/App';

import { getConversationTitle } from "@helpers/conversation";
import { getIdFromParamsOrLastPath, getLastPath, setLastPath } from "@helpers/lastPath";

import { fetchConversations } from '@services/ConversationsService';

import { useSession } from '@root/SessionContext';
import SuwaliErrorBoundary from '@root/SuwaliErrorBoundary';

import { useBidirectionalInfiniteScroll } from "@hooks/useBidirectionalInfiniteScroll";
import { useFilterAndSort } from '@hooks/useFilterAndSort';
import { useHubPath } from '@hooks/useHubPath';
import { useIsInTabletRange, useResponsiveDrawer } from '@hooks/useLayout';
import usePageTitle from '@hooks/usePageTitle';
import { usePaginatedListScroll } from "@hooks/usePaginatedListScroll";

import EmptyState from '@ui-components/layout/EmptyState';
import { Layout } from '@ui-components/layout/Layout';

import Fallback from '@layouts/error/Fallback';

import ConversationsSidebar from './ConversationsSidebar';
import ConversationStack from './ConversationStack';

const VALID_FROM_VALUES: ConversationFrom[] = ['audience', 'me', 'team'];

const pageTitles: Record<ConversationFrom, Parameters<typeof usePageTitle>[0]> = {
  audience: (
    <FormattedMessage
      id="global.audienceConversations"
      defaultMessage="Audience conversations"
      description="Reusable label for conversations with end-users page or list title"
    />
  ),
  team: (
    <FormattedMessage
      id="global.teamConversations"
      defaultMessage="Team conversations"
      description="Reusable label for team conversations page or list title"
    />
  ),
  me: (
    <FormattedMessage
      id="global.yourConversations"
      defaultMessage="Your conversations"
      description="Reusable label for your conversations page or list title"
    />
  ),
};

const ConversationsMain = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const hubId = useSession().currentHubId();
  const hubPath = useHubPath();
  const from: ConversationFrom = VALID_FROM_VALUES.includes(params.from as ConversationFrom)
    ? (params.from as ConversationFrom)
    : 'audience';

  // Helper to navigate to a conversation while preserving current filters
  const navigateToConversation = useCallback((conversationId: number, options?: { replace?: boolean }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('messageId'); // Clear messageId when navigating to a different conversation

    const query = searchParams.toString();
    const url = query
      ? `/conversations/${from}/${conversationId}?${query}`
      : `/conversations/${from}/${conversationId}`;
    navigate(hubPath(url), options);
  }, [from, location.search, navigate, hubPath]);

  const [mobileTab, setMobileTab] = useState('mobile-main');
  const isInTabletRange = useIsInTabletRange('501px', '850px');
  const [isLeftNavVisible, setIsLeftNavVisible] = useResponsiveDrawer('850px');
  const [settledFrom, setSettledFrom] = useState<string | null>(null);
  const selectedAssetSettled = settledFrom === from;
  // initialAroundId is re-resolved when "from changes" so it loads the correct conversation and scrolls it into view
  const initialAroundId = useMemo(() => {
    return getIdFromParamsOrLastPath(
      params.conversationId,
      `conversationsLastPath:${params.from ?? 'audience'}`,
      hubId,
      /\/conversations\/[^/]+\/(\d+)/
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We want to re-resolve initialAroundId when the "from" changes, so we intentionally omit hubId and params.conversationId from the dependency array
  }, [params.from]);

  const {
    currentQuery: conversationsFilter,
  } = useFilterAndSort({});

  usePageTitle(pageTitles[from]);

  const conversationsQuery = useBidirectionalInfiniteScroll<Conversation>({
    queryKey: [
      `/hubs/${hubId}/conversations`,
      `filter:${JSON.stringify({ ...conversationsFilter, from })}`,
    ],
    queryFn: (aroundId, windowSize) => fetchConversations(hubId!, aroundId, windowSize, { ...conversationsFilter, from }),
    initialAroundId,
    windowSize: PAGINATION_WINDOW_SIZE,
    enabled: Boolean(hubId),
  });

  if (conversationsQuery.error) throw conversationsQuery.error;

  const conversations = conversationsQuery.items;
  const isPending = conversationsQuery.isPending;
  const refetchAroundId = conversationsQuery.refetchAroundId;

  const selectedConversation = useMemo(() => {
    if (!params.conversationId) return null;
    return conversations.find(c => c.id === parseInt(params.conversationId!, 10)) ?? null;
  }, [conversations, params.conversationId]);

  // Re-resolve only when the URL has no conversation; resetting on every click swaps the sidebar for a skeleton and loses its scroll position
  useEffect(() => {
    if (!params.conversationId) setSettledFrom(null);
  }, [params.conversationId]);

  // Update conversationsLastPath whenever the URL changes (including filter changes)
  useEffect(() => {
    if (params.conversationId && selectedAssetSettled) {
      const pathWithParams = location.search
        ? `/conversations/${from}/${params.conversationId}${location.search}`
        : `/conversations/${from}/${params.conversationId}`;
      setLastPath('conversationsLastPath', hubId, pathWithParams);
      setLastPath(`conversationsLastPath:${from}`, hubId, pathWithParams);
    }
  }, [params.conversationId, from, location.search, selectedAssetSettled, hubId]);

  // After settling, if the selected conversation has been filtered out, navigate to the first available
  useEffect(() => {
    if (!isPending && selectedAssetSettled && params.conversationId && !selectedConversation && conversations.length > 0) {
      navigateToConversation(conversations[0].id, { replace: true });
    }
  }, [selectedConversation, conversations, isPending, navigateToConversation, selectedAssetSettled, params.conversationId]);

  // Initial setup: resolve which conversation to navigate to on first load or view switch
  useEffect(() => {
    if (isPending || selectedAssetSettled) return;

    const conversationId = params.conversationId ?? '';
    // Skip setting lastPath if has_message date range filter is active — prioritize showing the
    // most recent conversation instead of navigating to a potentially irrelevant lastPath
    const lastPath = !conversationId && !conversationsFilter.has_message
      ? getLastPath(`conversationsLastPath:${from}`, hubId)
      : null;

    if (conversations.length === 0) {
      setSettledFrom(from);
    } else if (conversationId) {
      const pathWithParams = location.search
        ? `/conversations/${from}/${conversationId}${location.search}`
        : `/conversations/${from}/${conversationId}`;
      setLastPath('conversationsLastPath', hubId, pathWithParams);
      setLastPath(`conversationsLastPath:${from}`, hubId, pathWithParams);
      setMobileTab('mobile-main');
      setSettledFrom(from);
    } else if (lastPath) {
      navigate(hubPath(lastPath), { replace: true });
    } else {
      const url = location.search
        ? `/conversations/${from}/${conversations[0].id}${location.search}`
        : `/conversations/${from}/${conversations[0].id}`;
      navigate(hubPath(url), { replace: true });
      setMobileTab('mobile-main');
    }
  }, [params.conversationId, from, conversations, isPending, navigate, selectedAssetSettled, location.search, conversationsFilter.has_message, hubId, hubPath]);

  // Handle scroll behavior for paginated list
  const { topObserverTarget, bottomObserverTarget } = usePaginatedListScroll({
    containerElementId: 'conversations-sidebar-list',
    itemIdPrefix: 'conversation',
    query: conversationsQuery,
  });

  return (
    <Layout
      leftDrawerOpen={isLeftNavVisible}
      onLeftDrawerOpenChange={setIsLeftNavVisible}
      mobileTab={mobileTab}
      onMobileTabChange={setMobileTab}
    >
      <Layout.Left
        name={
          <FormattedMessage
            id="global.list"
            defaultMessage="List"
            description="Menu item for mobile tab navigation that indicates if the user selects this item it will show the list"
          />
        }
      >
        <div className="layout-left-column-content">
          <SuwaliErrorBoundary fallback={<Fallback actions={['tryAgain']} />}>
            <ConversationsSidebar
              from={from}
              isPending={!selectedAssetSettled}
              topObserverTarget={topObserverTarget}
              bottomObserverTarget={bottomObserverTarget}
              activeConversationId={selectedConversation?.id || null}
              conversations={conversations}
              conversationTitleMapper={from === 'me' ? (conversation) => conversation.title : getConversationTitle}
              refetchAroundId={refetchAroundId}
              onConversationSelect={(conversation) => {
                navigateToConversation(conversation.id);
                setMobileTab('mobile-main');
                if (isInTabletRange) {
                  setIsLeftNavVisible(false);
                }
              }}
            />
          </SuwaliErrorBoundary>
        </div>
      </Layout.Left>
      <Layout.Main
        name={
          <FormattedMessage
            id="global.conversation"
            defaultMessage="Conversation"
            description="Reusable label for Conversation page or title"
          />
        }
      >
        <SuwaliErrorBoundary fallback={<Fallback actions={['contactSupport', 'backHome', 'tryAgain']} />}>
          { !selectedConversation && <EmptyState icon="conversations" /> }
          { selectedConversation &&
            <ConversationStack
              className="layout-main-column-scrollable-area"
              from={from}
              conversation={selectedConversation}
              refetchAroundId={refetchAroundId}
            />
          }
        </SuwaliErrorBoundary>
      </Layout.Main>
    </Layout>
  );
};

export default ConversationsMain;
